import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast as sonnerToast } from 'sonner';

import { Modal, Button } from './ui';
import { BugReportImagePicker } from './BugReportImagePicker';
import { apiClient } from '../lib/api';
import { toast } from '../lib/toast';
import type { BugReportPrefill } from '../contexts/BugReportContext';

import styles from './BugReportModal.module.css';

const MIN_TITLE = 3;
const MIN_DESCRIPTION = 10;
const MAX_TITLE = 200;
const MAX_DESCRIPTION = 5000;

export interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  prefill?: BugReportPrefill | null;
}

interface FormState {
  title: string;
  description: string;
  file: File | null;
  previewUrl: string | null;
  submitting: boolean;
}

const INITIAL_STATE: FormState = {
  title: '',
  description: '',
  file: null,
  previewUrl: null,
  submitting: false,
};

function buildErrorMessage(status: number, body: { error?: string; code?: string }): string {
  if (body.error) return body.error;
  if (status === 413) return 'Imagem maior que 5 MB.';
  if (status === 429) return 'Muitos reports recentes. Tente novamente em alguns minutos.';
  return 'Não foi possível enviar o bug. Tente novamente.';
}

export function BugReportModal({ isOpen, onClose, prefill }: BugReportModalProps) {
  const [state, setState] = useState<FormState>(INITIAL_STATE);

  // Apply prefill when modal opens; reset everything when it closes.
  useEffect(() => {
    if (!isOpen) {
      setState((prev) => {
        if (prev.previewUrl) URL.revokeObjectURL(prev.previewUrl);
        return INITIAL_STATE;
      });
      return;
    }
    setState((prev) => ({
      ...prev,
      title: prefill?.title ?? '',
      description: prefill?.description ?? '',
    }));
  }, [isOpen, prefill]);

  // Cleanup preview URL on unmount.
  useEffect(() => {
    return () => {
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSubmit = useMemo(() => {
    if (state.submitting) return false;
    if (state.title.trim().length < MIN_TITLE) return false;
    if (state.description.trim().length < MIN_DESCRIPTION) return false;
    return true;
  }, [state.submitting, state.title, state.description]);

  const handleFileChange = useCallback((next: File | null) => {
    setState((prev) => {
      if (prev.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return {
        ...prev,
        file: next,
        previewUrl: next ? URL.createObjectURL(next) : null,
      };
    });
  }, []);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setState((prev) => ({ ...prev, submitting: true }));
    try {
      const form = new FormData();
      form.append('title', state.title.trim().slice(0, MAX_TITLE));
      form.append('description', state.description.trim().slice(0, MAX_DESCRIPTION));
      form.append('contextUrl', `${window.location.pathname}${window.location.search}`);
      form.append('userAgent', navigator.userAgent);
      if (prefill?.errorRequestId) form.append('errorRequestId', prefill.errorRequestId);
      if (prefill?.errorMessage) form.append('errorMessage', prefill.errorMessage);
      if (state.file) form.append('image', state.file);

      const res = await apiClient('/bugs', { method: 'POST', body: form });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
        throw new Error(buildErrorMessage(res.status, body));
      }

      toast.success('Bug reportado! Vamos investigar.');
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao enviar bug';
      sonnerToast.error(message);
    } finally {
      setState((prev) => ({ ...prev, submitting: false }));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reportar bug" size="lg">
      <div className={styles.form}>
        <div className={styles.section}>
          <label htmlFor="bug-title" className={styles.label}>
            Título do bug
          </label>
          <input
            id="bug-title"
            type="text"
            className={styles.input}
            value={state.title}
            onChange={(e) => setState((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Resumo curto (ex.: Botão de salvar não funciona em /assinatura)"
            maxLength={MAX_TITLE}
            required
          />
          <span className={styles.hint}>Mínimo 3 caracteres.</span>
        </div>

        <div className={styles.section}>
          <label htmlFor="bug-description" className={styles.label}>
            Descreva o que aconteceu
          </label>
          <textarea
            id="bug-description"
            className={styles.textarea}
            value={state.description}
            onChange={(e) => setState((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="O que você estava fazendo? O que esperava? O que aconteceu?"
            rows={5}
            maxLength={MAX_DESCRIPTION}
            required
          />
          <span className={styles.hint}>Mínimo 10 caracteres.</span>
        </div>

        <BugReportImagePicker
          file={state.file}
          previewUrl={state.previewUrl}
          onChange={handleFileChange}
        />

        <div className={styles.actions}>
          <Button variant="ghost" onClick={onClose} disabled={state.submitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={state.submitting}
            disabled={!canSubmit}
          >
            Enviar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
